import { useViewMode, type ViewMode } from '@/stores/viewMode'
import { useConfig } from '@/hooks/useConfig'
import { Skeleton } from '@/components/ui/skeleton'
import { Building, Building2, Store } from 'lucide-react'

const MODE_META: Record<
  Exclude<ViewMode, 'default' | 'admin'>,
  { label: string; Icon: React.ElementType }
> = {
  provider: { label: '組織', Icon: Building2 },
  clinic: { label: '診所', Icon: Building },
  clinic_group: { label: '診所集團', Icon: Building2 },
  ad: { label: '供應商', Icon: Store },
  ad_supplier_group: { label: '供應商集團', Icon: Store },
}

export default function Logo() {
  const { mode, id } = useViewMode()

  const { branding, isLoading } = useConfig({
    viewMode: mode,
    viewModeId: id,
  })

  if (isLoading) {
    return (
      <div className="px-6 pb-6">
        <Skeleton className="h-12 w-32" />
      </div>
    )
  }

  // admin (sys_admin 後台) 與 default (investor 等無 scope) 用系統 logo
  if (mode === 'admin' || mode === 'default') {
    return (
      <div className="px-6 pb-6">
        <div className="flex items-center justify-start">
          <img src="/images/xlogo.png" alt="logo" className="h-12 w-auto" />
        </div>
      </div>
    )
  }

  const meta = MODE_META[mode]
  const Icon = meta?.Icon ?? Building2
  const label = meta?.label

  // If branding has logo image, show logo + name stack; otherwise icon + name
  const logoSrc = branding?.logoUrl
    ? branding.logoUrl.startsWith('http')
      ? branding.logoUrl
      : `${import.meta.env.VITE_API_URL}${branding.logoUrl}`
    : null

  return (
    <div className="px-6 pb-6">
      <div className="flex items-center gap-3">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={branding?.name ?? label}
            className="h-12 w-12 object-contain"
          />
        ) : (
          <Icon className="h-8 w-8 text-muted-foreground" />
        )}
        <div className="flex flex-col min-w-0">
          {label && (
            <span className="text-xs text-muted-foreground leading-tight">{label}</span>
          )}
          <span className="text-lg font-semibold truncate max-w-[150px] leading-tight">
            {branding?.name || 'System'}
          </span>
        </div>
      </div>
    </div>
  )
}
